/**
 * Finance transactions list (FIN-10).
 *
 * Filter bar (account, category + subtree, date range, text, uncategorized,
 * transfer-leg visibility) → paginated table. Each row can be edited (modal)
 * or categorized inline via a category combobox over the tree. Multi-select
 * supports shift-click ranges and drives a bulk-categorize action bar.
 *
 * After any manual categorization a transient "Always do this → create rule"
 * affordance appears; clicking it opens a prefilled rule dialog
 * (`description contains <text>` → the chosen category, applyNow checked).
 */

import { useMemo, useRef, useState } from 'react';
import {
  useFinanceTransactions,
  useFinanceCategories,
  useFinanceAccounts,
  usePatchFinanceTransaction,
  useArchiveFinanceTransaction,
  useBulkCategorizeTransactions,
  useMoveFinanceTransactions,
  fetchFilteredTransactions,
  TRANSACTIONS_BULK_CAP,
  type FinanceTransactionDto,
  type FinanceAccountDto,
  type TransactionListParams,
} from '../../api/finance';
import { ConfirmDialog } from '../../components/finance/ConfirmDialog';
import {
  formatEur,
  formatNative,
  buildCategoryOptions,
  accountMoveTargets,
  financeErrorMessage,
  type CategoryOption,
} from './finance-format';
import { RuleFromCategorizationDialog } from '../../components/finance/RuleFromCategorizationDialog';
import { TransactionEditModal } from '../../components/finance/TransactionEditModal';
import { ExternalTransferModal } from '../../components/finance/ExternalTransferModal';
import { CategoryPicker } from '../../components/finance/CategoryPicker';
import { Icon } from '@k-os/ui';
import styles from './finance.module.css';

const PAGE_SIZE = 100;

interface Filters {
  accountId: string;
  categoryId: string;
  includeSubtree: boolean;
  from: string;
  to: string;
  q: string;
  amountMin: string;
  amountMax: string;
  uncategorized: boolean;
  transferLegs: '' | 'only' | 'exclude';
}

const emptyFilters = (): Filters => ({
  accountId: '',
  categoryId: '',
  includeSubtree: true,
  from: '',
  to: '',
  q: '',
  amountMin: '',
  amountMax: '',
  uncategorized: false,
  transferLegs: '',
});

/** A just-categorized transaction we can offer a "make a rule" affordance for. */
interface RuleSeed {
  description: string;
  categoryId: string;
}

export function FinanceTransactionsScreen() {
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [page, setPage] = useState(0);

  const accountsQ = useFinanceAccounts({ includeArchived: true });
  const categoriesQ = useFinanceCategories();
  const accounts = useMemo(() => accountsQ.data ?? [], [accountsQ.data]);
  const categoryOptions = useMemo(
    () => buildCategoryOptions(categoriesQ.data ?? []),
    [categoriesQ.data],
  );
  const accountName = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts) m.set(a.id, a.name);
    return m;
  }, [accounts]);
  const params: TransactionListParams = {
    ...(filters.accountId ? { accountId: filters.accountId } : {}),
    ...(filters.categoryId ? { categoryId: filters.categoryId, includeSubtree: filters.includeSubtree } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.amountMin !== '' && Number.isFinite(Number(filters.amountMin))
      ? { amountMin: Number(filters.amountMin) }
      : {}),
    ...(filters.amountMax !== '' && Number.isFinite(Number(filters.amountMax))
      ? { amountMax: Number(filters.amountMax) }
      : {}),
    ...(filters.uncategorized ? { uncategorized: true } : {}),
    ...(filters.transferLegs ? { transferLegs: filters.transferLegs } : {}),
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const txQ = useFinanceTransactions(params);
  const transactions = useMemo(() => txQ.data?.transactions ?? [], [txQ.data]);
  const total = txQ.data?.total ?? 0;
  const accountCurrency = (id: string) => accounts.find((a) => a.id === id)?.currency ?? 'EUR';

  // Selection (shift-click ranges over the current page).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const lastClickedIndex = useRef<number | null>(null);
  // When "select all matching" is used, holds the fetched cross-page rows so we
  // can resolve their account ids (for bulk move) and show all-matching state.
  const [allMatching, setAllMatching] = useState<FinanceTransactionDto[] | null>(null);
  const [selectingAll, setSelectingAll] = useState(false);

  const clearSelection = () => {
    setSelected(new Set());
    setAllMatching(null);
    lastClickedIndex.current = null;
  };

  const onRowSelect = (index: number, id: string, shift: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && lastClickedIndex.current !== null) {
        const [a, b] = [lastClickedIndex.current, index].sort((x, y) => x - y);
        for (let i = a; i <= b; i++) {
          const t = transactions[i];
          if (t) next.add(t.id);
        }
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    lastClickedIndex.current = index;
  };

  // Edit modal + rule affordance + delete confirm.
  const [editing, setEditing] = useState<FinanceTransactionDto | null>(null);
  const [deleting, setDeleting] = useState<FinanceTransactionDto | null>(null);
  const [ruleSeed, setRuleSeed] = useState<RuleSeed | null>(null);
  const [ruleDialog, setRuleDialog] = useState<RuleSeed | null>(null);
  // Transactions to mark as external (one-sided) transfers (ADR 0039).
  const [externalIds, setExternalIds] = useState<string[] | null>(null);

  const patch = usePatchFinanceTransaction();
  const onInlineCategorize = (t: FinanceTransactionDto, categoryId: string) => {
    if (!categoryId || categoryId === t.categoryId) return;
    patch.mutate(
      { id: t.id, patch: { categoryId } },
      {
        onSuccess: () => setRuleSeed({ description: t.description, categoryId }),
      },
    );
  };

  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setPage(0);
    clearSelection();
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // id → accountId across the current page AND any "select all matching" fetch,
  // so bulk-move's same-account check is correct even for cross-page selections.
  const idToAccount = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of transactions) m.set(t.id, t.accountId);
    for (const t of allMatching ?? []) m.set(t.id, t.accountId);
    return m;
  }, [transactions, allMatching]);

  // Bulk move targets: only when every selected row is in the SAME account
  // (so the parent/child relationship is well-defined).
  const selectedAccountIds = useMemo(() => {
    const s = new Set<string>();
    for (const id of selected) {
      const a = idToAccount.get(id);
      if (a) s.add(a);
    }
    return [...s];
  }, [idToAccount, selected]);
  const crossAccountSelection = selectedAccountIds.length > 1;

  // Page-level + all-matching selection helpers.
  const allOnPageSelected =
    transactions.length > 0 && transactions.every((t) => selected.has(t.id));
  const selectedAllMatching = allMatching !== null;

  const toggleAllOnPage = () => {
    if (selectedAllMatching) {
      clearSelection();
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (transactions.every((t) => next.has(t.id))) {
        for (const t of transactions) next.delete(t.id);
      } else {
        for (const t of transactions) next.add(t.id);
      }
      return next;
    });
    lastClickedIndex.current = null;
  };

  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const res = await fetchFilteredTransactions(params);
      setAllMatching(res.transactions);
      setSelected(new Set(res.transactions.map((t) => t.id)));
    } finally {
      setSelectingAll(false);
    }
  };
  const bulkMoveTargets = useMemo(
    () =>
      selectedAccountIds.length === 1 ? accountMoveTargets(accounts, selectedAccountIds[0]!) : [],
    [accounts, selectedAccountIds],
  );

  // "Mark as external" only applies to rows that aren't already transfer legs.
  // Resolve leg-state across the current page AND any "select all matching"
  // fetch, so a cross-page selection is judged correctly.
  const idToTransferId = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const t of transactions) m.set(t.id, t.transferId);
    for (const t of allMatching ?? []) m.set(t.id, t.transferId);
    return m;
  }, [transactions, allMatching]);
  const selectionHasTransferLeg = useMemo(
    () => [...selected].some((id) => (idToTransferId.get(id) ?? null) !== null),
    [idToTransferId, selected],
  );

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Transactions</h2>
          <div className={styles.subtitle}>
            Filter, categorize inline, and bulk-apply. Categorizing a row offers
            a one-click rule so the next import handles it for you.
          </div>
        </div>
      </header>

      {/* ─── Filter bar ──────────────────────────────────────────────── */}
      <div className={styles.filterBar}>
        <select
          className={styles.filterControl}
          value={filters.accountId}
          onChange={(e) => setFilter('accountId', e.target.value)}
          aria-label="Account"
        >
          <option value="">All accounts</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>

        <select
          className={styles.filterControl}
          value={filters.categoryId}
          onChange={(e) => setFilter('categoryId', e.target.value)}
          aria-label="Category"
        >
          <option value="">All categories</option>
          {categoryOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>

        {filters.categoryId && (
          <button
            type="button"
            className={`${styles.toggle} ${filters.includeSubtree ? styles.toggleOn : ''}`}
            onClick={() => setFilter('includeSubtree', !filters.includeSubtree)}
            title="Include child categories"
          >
            + subtree
          </button>
        )}

        <input
          type="date"
          className={styles.filterControl}
          value={filters.from}
          onChange={(e) => setFilter('from', e.target.value)}
          aria-label="From date"
        />
        <input
          type="date"
          className={styles.filterControl}
          value={filters.to}
          onChange={(e) => setFilter('to', e.target.value)}
          aria-label="To date"
        />

        <div className={styles.searchWrap}>
          <Icon name="search" size={14} />
          <input
            className={styles.searchInput}
            placeholder="Search description…"
            value={filters.q}
            onChange={(e) => setFilter('q', e.target.value)}
          />
        </div>

        <input
          className={styles.filterControl}
          style={{ width: 110 }}
          inputMode="decimal"
          placeholder="Min €"
          value={filters.amountMin}
          onChange={(e) => setFilter('amountMin', e.target.value)}
          aria-label="Minimum EUR amount (signed)"
          title="Signed EUR amount. Min 0 → only income (≥ 0)."
        />
        <input
          className={styles.filterControl}
          style={{ width: 110 }}
          inputMode="decimal"
          placeholder="Max €"
          value={filters.amountMax}
          onChange={(e) => setFilter('amountMax', e.target.value)}
          aria-label="Maximum EUR amount (signed)"
          title="Signed EUR amount. Max 0 → only expenses (≤ 0)."
        />

        <button
          type="button"
          className={`${styles.toggle} ${filters.uncategorized ? styles.toggleOn : ''}`}
          onClick={() => setFilter('uncategorized', !filters.uncategorized)}
        >
          Uncategorized only
        </button>

        <select
          className={styles.filterControl}
          value={filters.transferLegs}
          onChange={(e) => setFilter('transferLegs', e.target.value as Filters['transferLegs'])}
          aria-label="Transfer legs"
        >
          <option value="">Show transfer legs</option>
          <option value="exclude">Hide transfer legs</option>
          <option value="only">Only transfer legs</option>
        </select>

        <button type="button" className={styles.toggle} onClick={() => { setFilters(emptyFilters()); setPage(0); clearSelection(); }}>
          Reset
        </button>
      </div>

      {/* ─── Table ───────────────────────────────────────────────────── */}
      {txQ.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : txQ.isError ? (
        <div className={styles.error}>{financeErrorMessage(txQ.error)}</div>
      ) : transactions.length === 0 ? (
        <div className={styles.empty}>No transactions match these filters.</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={allOnPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = !allOnPageSelected && selected.size > 0;
                    }}
                    onChange={toggleAllOnPage}
                  />
                </th>
                <th>Date</th>
                <th>Description</th>
                <th>Account</th>
                <th>Category</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ width: 44 }} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {transactions.map((t, i) => {
                const isTransfer = t.transferId !== null;
                const native = accountCurrency(t.accountId);
                const isEur = native === 'EUR';
                return (
                  <tr key={t.id} className={selected.has(t.id) ? styles.rowSelected : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(t.id)}
                        onChange={(e) =>
                          onRowSelect(i, t.id, (e.nativeEvent as MouseEvent).shiftKey)
                        }
                        onClick={(e) => {
                          if (e.shiftKey) e.preventDefault();
                        }}
                      />
                    </td>
                    <td className={styles.num} style={{ textAlign: 'left' }}>
                      {t.date}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.descBtn}
                        onClick={() => setEditing(t)}
                        title="Edit transaction"
                      >
                        {t.description}
                      </button>
                      {isTransfer && (
                        <span className={`${styles.badge} ${styles.badgeTransfer}`}>
                          <Icon name="link" size={10} /> transfer
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={styles.owner}>{accountName.get(t.accountId) ?? '—'}</span>
                    </td>
                    <td>
                      {isTransfer ? (
                        <span className={styles.muted}>—</span>
                      ) : (
                        <CategoryPicker
                          categories={categoriesQ.data ?? []}
                          value={t.categoryId}
                          kind={t.amount < 0 ? 'expense' : 'income'}
                          placeholder="Uncategorized…"
                          ariaLabel={`Category for ${t.description}`}
                          compact
                          onChange={(catId) => onInlineCategorize(t, catId)}
                        />
                      )}
                    </td>
                    <td
                      className={`${styles.num} ${t.amount < 0 ? styles.amtNeg : styles.amtPos}`}
                    >
                      <div>{formatEur(t.amountEur)}</div>
                      {!isEur && (
                        <div className={styles.numSub}>{formatNative(t.amount, native)}</div>
                      )}
                    </td>
                    <td>
                      {!isTransfer && (
                        <button
                          type="button"
                          className={styles.iconBtn}
                          onClick={() => setExternalIds([t.id])}
                          title="Mark as external transfer (untracked counterparty)"
                        >
                          <Icon name="link" size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Pagination ──────────────────────────────────────────────── */}
      {total > 0 && (
        <div className={styles.pager}>
          <span className={styles.fieldHint}>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </span>
          <div className={styles.rowActions}>
            <button
              type="button"
              className={styles.iconBtn}
              disabled={page === 0}
              onClick={() => { setPage((p) => Math.max(0, p - 1)); clearSelection(); }}
            >
              Prev
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              disabled={page >= pageCount - 1}
              onClick={() => { setPage((p) => p + 1); clearSelection(); }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ─── Bulk action bar ─────────────────────────────────────────── */}
      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          options={categoryOptions}
          ids={[...selected]}
          moveTargets={bulkMoveTargets}
          crossAccount={crossAccountSelection}
          canMarkExternal={!selectionHasTransferLeg}
          onMarkExternal={() => setExternalIds([...selected])}
          total={total}
          pageRowCount={transactions.length}
          allMatchingMode={selectedAllMatching}
          selectingAll={selectingAll}
          onSelectAllMatching={selectAllMatching}
          onClear={clearSelection}
        />
      )}

      {/* ─── Rule-from-categorization transient affordance ───────────── */}
      {ruleSeed && (
        <div className={styles.ruleToast}>
          <Icon name="sparkles" size={14} />
          <span>Categorized. Always do this for similar transactions?</span>
          <button
            type="button"
            className="kos-btn kos-btn-primary"
            onClick={() => {
              setRuleDialog(ruleSeed);
              setRuleSeed(null);
            }}
          >
            Create rule
          </button>
          <button type="button" className={styles.linkBtn} onClick={() => setRuleSeed(null)}>
            Dismiss
          </button>
        </div>
      )}

      <TransactionEditModal
        transaction={editing}
        categories={categoriesQ.data ?? []}
        accounts={accounts}
        onClose={() => setEditing(null)}
        onDelete={() => {
          const t = editing;
          setEditing(null);
          setDeleting(t);
        }}
      />

      <DeleteTransactionDialog
        transaction={deleting}
        onClose={() => setDeleting(null)}
      />

      <RuleFromCategorizationDialog
        seed={ruleDialog}
        categories={categoriesQ.data ?? []}
        onClose={() => setRuleDialog(null)}
      />

      <ExternalTransferModal
        transactionIds={externalIds}
        onClose={() => setExternalIds(null)}
        onDone={clearSelection}
      />
    </div>
  );
}

// ─── Delete confirm ──────────────────────────────────────────────────────────

function DeleteTransactionDialog({
  transaction,
  onClose,
}: {
  transaction: FinanceTransactionDto | null;
  onClose: () => void;
}) {
  const archive = useArchiveFinanceTransaction();
  const open = !!transaction;

  const [seen, setSeen] = useState<string | null>(null);
  if (open && transaction.id !== seen) {
    setSeen(transaction.id);
    archive.reset();
  }
  if (!open && seen !== null) setSeen(null);

  return (
    <ConfirmDialog
      open={open}
      title="Delete transaction"
      message={
        transaction
          ? `Delete “${transaction.description}” (${formatEur(transaction.amountEur)})? It's removed from the list and from all balances and analytics. This can't be undone from the UI.`
          : ''
      }
      confirmLabel={archive.isPending ? 'Deleting…' : 'Delete'}
      confirmDisabled={archive.isPending}
      destructive
      error={financeErrorMessage(archive.error)}
      onClose={onClose}
      onConfirm={() => {
        if (!transaction) return;
        archive.mutate(transaction.id, { onSuccess: onClose });
      }}
    />
  );
}

// ─── Bulk-categorize action bar ──────────────────────────────────────────────

function BulkBar({
  count,
  ids,
  options,
  moveTargets,
  crossAccount,
  canMarkExternal,
  onMarkExternal,
  total,
  pageRowCount,
  allMatchingMode,
  selectingAll,
  onSelectAllMatching,
  onClear,
}: {
  count: number;
  ids: string[];
  options: CategoryOption[];
  moveTargets: FinanceAccountDto[];
  crossAccount: boolean;
  /** False when any selected row is already a transfer leg. */
  canMarkExternal: boolean;
  onMarkExternal: () => void;
  total: number;
  pageRowCount: number;
  allMatchingMode: boolean;
  selectingAll: boolean;
  onSelectAllMatching: () => void;
  onClear: () => void;
}) {
  const bulk = useBulkCategorizeTransactions();
  const move = useMoveFinanceTransactions();
  const [categoryId, setCategoryId] = useState('');
  const [moveTo, setMoveTo] = useState('');

  const apply = () => {
    if (!categoryId) return;
    bulk.mutate(
      { transactionIds: ids, categoryId },
      { onSuccess: () => { setCategoryId(''); onClear(); } },
    );
  };

  const applyMove = () => {
    if (!moveTo) return;
    move.mutate(
      { transactionIds: ids, targetAccountId: moveTo },
      { onSuccess: () => { setMoveTo(''); onClear(); } },
    );
  };

  return (
    <div className={styles.bulkBar}>
      <span className={styles.bulkCount}>
        {count} selected{allMatchingMode ? ' (all matching)' : ''}
      </span>
      {!allMatchingMode && total > pageRowCount && (
        <button
          type="button"
          className={styles.linkBtn}
          disabled={selectingAll}
          onClick={onSelectAllMatching}
        >
          {selectingAll ? 'Selecting…' : `Select all ${total} matching`}
        </button>
      )}
      {allMatchingMode && total > TRANSACTIONS_BULK_CAP && (
        <span className={styles.fieldHint}>(first {TRANSACTIONS_BULK_CAP} of {total})</span>
      )}
      <select
        className={styles.select}
        value={categoryId}
        onChange={(e) => setCategoryId(e.target.value)}
        aria-label="Bulk category"
      >
        <option value="">Categorize as…</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="kos-btn kos-btn-primary"
        disabled={!categoryId || bulk.isPending}
        onClick={apply}
      >
        {bulk.isPending ? 'Applying…' : `Apply to ${count}`}
      </button>

      {crossAccount ? (
        <span className={styles.fieldHint}>Move: select one account’s rows</span>
      ) : moveTargets.length > 0 ? (
        <>
          <select
            className={styles.select}
            value={moveTo}
            onChange={(e) => setMoveTo(e.target.value)}
            aria-label="Bulk move target"
          >
            <option value="">Move to…</option>
            {moveTargets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="kos-btn"
            disabled={!moveTo || move.isPending}
            onClick={applyMove}
          >
            {move.isPending ? 'Moving…' : `Move ${count}`}
          </button>
        </>
      ) : null}

      <button
        type="button"
        className="kos-btn"
        disabled={!canMarkExternal}
        onClick={onMarkExternal}
        title={
          canMarkExternal
            ? 'Mark selected as external transfers (untracked counterparty)'
            : 'Some selected rows are already transfer legs'
        }
      >
        Mark as external transfer
      </button>

      <button type="button" className="kos-btn" onClick={onClear}>
        Clear
      </button>
      {bulk.isError && (
        <span className={styles.fieldError}>{financeErrorMessage(bulk.error)}</span>
      )}
      {move.isError && (
        <span className={styles.fieldError}>{financeErrorMessage(move.error)}</span>
      )}
    </div>
  );
}
